// Vendors
import React, { useState, useContext, createContext, useMemo } from 'react';
import { ReactChildrenType } from '@openbank/cf-ui-static-data';
// Constants
import { EMPTY_STRING } from 'utils/constants/utils.constants';
import { CONTEXT_DEFAULT_VALUE } from './constants/app.context.constants';
// Enumerations
import { BannerTypeEnumeration } from 'enumerations/banner/banner.enumeration';
// Types
import type { AppContextType } from './types/app.context.type';
import type {
  CardDetailComponentType,
  CardComponentType,
  CardBaseComponentType,
} from 'types/card.component.type';
import type { SelectedCardType } from 'types/selected-card.type';
import type { LocalizationConfigurationType } from 'types/localization-configuration.type';
import type { PreapprovalInfoResponse } from 'types/credit/preapproval-info-response.type';
import type { FlexibleMinimumPaymentResponseType } from 'types/flexible-minimum-payment/flexible-minimum-payment-response.type';

const AppContext = createContext<AppContextType>(CONTEXT_DEFAULT_VALUE);

export const AppContextProvider = ({
  children,
}: ReactChildrenType): React.ReactElement<React.Provider<AppContextType>> => {
  const [cards, setCards] = useState<Array<CardBaseComponentType>>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [currentCard, setCurrentCard] = useState<CardComponentType | Record<string, any>>({});
  const selectedCard = useMemo<SelectedCardType | null>(
    () =>
      currentCard?.cardId
        ? {
            cardContractId: currentCard.cardContractId ?? EMPTY_STRING,
            cardId: currentCard.cardId,
          }
        : null,
    [currentCard?.cardId, currentCard?.cardContractId]
  );
  const [movementsLayoutAllowed, setMovementsLayoutAllowed] = useState(false);
  const [currentCardDetail, setCurrentCardDetail] = useState<
    CardDetailComponentType | Record<string, any>
  >({});
  const [featureFlags, setFeatureFlags] = useState<any[]>([]);
  const [languagePreference, setLanguagePreference] = useState<string | null>(null);
  const [localization, setLocalization] = useState<
    LocalizationConfigurationType | Record<string, any>
  >({});
  const [bannerType, setBannerType] = useState<BannerTypeEnumeration | null>(null);
  const [openRequestSignatureModal, setOpenRequestSignatureModal] = useState<boolean>(false);
  const [preapprovalInfo, setPreapprovalInfo] = useState<PreapprovalInfoResponse | null>(null);
  const [flexibleMinimumPaymentInfo, setFlexibleMinimumPaymentInfo] =
    useState<FlexibleMinimumPaymentResponseType | null>(null);
  const [profile, setProfile] = useState<any>({});

  return (
    <AppContext.Provider
      value={{
        bannerType,
        cards,
        currentCard,
        selectedCard,
        movementsLayoutAllowed,
        currentCardDetail,
        customerId,
        featureFlags,
        languagePreference,
        localization,
        openRequestSignatureModal,
        preapprovalInfo,
        profile,
        flexibleMinimumPaymentInfo,
        setBannerType,
        setCards,
        setCurrentCard,
        setMovementsLayoutAllowed,
        setCurrentCardDetail,
        setCustomerId,
        setFeatureFlags,
        setLanguagePreference,
        setLocalization,
        setOpenRequestSignatureModal,
        setPreapprovalInfo,
        setProfile,
        setFlexibleMinimumPaymentInfo,
      }}
    >
      {children as React.ReactNode}
    </AppContext.Provider>
  );
};

export const AppContextConsumerHook = (): AppContextType => useContext(AppContext);

/* eslint-disable */
export const AppContextConsumerHOC =
  (Component: React.FunctionComponent<unknown>): React.FunctionComponent<unknown> =>
  (props: any): React.ReactElement<React.Provider<AppContextType>> =>
    (
      <AppContextProvider>
        <Component {...props} />
      </AppContextProvider>
    );
